import { create } from 'zustand'
import { cartApi } from '../lib/api'
import { calculateRentalPrice, calculateServiceFee, calculateTotalAmount } from '../utils/pricing'

export interface CartItem {
  id: string
  itemId: string
  itemName: string
  itemImage: string
  dailyPrice: number
  weeklyPrice: number | null
  monthlyPrice: number | null
  itemSubtotal: number
  deposit: number
  serviceFee: number
  ownerId: string
  ownerName: string
  startDate: string
  endDate: string
  days: number
  totalPrice: number
  deliveryOption: 'pickup' | 'shipping' | 'doordash'
}

interface CartState {
  items: CartItem[]
  loading: boolean
  addItem: (item: CartItem) => Promise<void>
  removeItem: (id: string) => Promise<void>
  clearCart: () => Promise<void>
  syncCart: () => Promise<void>
  getTotal: () => number
  getItemCount: () => number
}

function mapServerItem(serverItem: any): CartItem {
  const dailyPrice = parseFloat(serverItem.item?.dailyPrice || '0')
  const weeklyPrice = serverItem.item?.weeklyPrice ? parseFloat(serverItem.item.weeklyPrice) : null
  const monthlyPrice = serverItem.item?.monthlyPrice ? parseFloat(serverItem.item.monthlyPrice) : null
  const deposit = parseFloat(serverItem.item?.securityDeposit || '0')
  const startDate = serverItem.startDate ? new Date(serverItem.startDate).toISOString().split('T')[0] : ''
  const endDate = serverItem.endDate ? new Date(serverItem.endDate).toISOString().split('T')[0] : ''

  let days = 0
  if (startDate && endDate) {
    const start = new Date(startDate)
    const end = new Date(endDate)
    days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
  }

  const photos = (serverItem.item?.photos as any[]) || []
  const firstPhoto = photos.find((p: any) => p.isDefault) || photos[0]
  const itemImage = firstPhoto?.url || ''

  const owner = serverItem.item?.owner
  const ownerName = owner ? `${owner.firstName} ${owner.lastName?.[0] || ''}.` : ''

  const itemSubtotal = calculateRentalPrice(dailyPrice, weeklyPrice, monthlyPrice, days)
  const serviceFee = calculateServiceFee(itemSubtotal)

  return {
    id: serverItem.id,
    itemId: serverItem.itemId,
    itemName: serverItem.item?.title || '',
    itemImage,
    dailyPrice,
    weeklyPrice,
    monthlyPrice,
    itemSubtotal,
    deposit,
    serviceFee,
    ownerId: serverItem.item?.owner?.id || '',
    ownerName,
    startDate,
    endDate,
    days,
    totalPrice: calculateTotalAmount(itemSubtotal, serviceFee, deposit),
    deliveryOption: (serverItem.deliveryOption || 'pickup') as 'pickup' | 'shipping' | 'doordash',
  }
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  loading: false,

  syncCart: async () => {
    set({ loading: true })
    try {
      const res = await cartApi.get()
      if (res.data.success) {
        const cart = res.data.data
        const items = (cart.items || []).map(mapServerItem)
        set({ items, loading: false })
      } else {
        set({ loading: false })
      }
    } catch {
      set({ loading: false })
    }
  },

  addItem: async (item) => {
    // Deliberately no local-state fallback on failure: the cart is
    // server-authoritative (see CLAUDE.md's Cart Storage Rules) and the Cart
    // page re-syncs from the server on every mount, which immediately
    // overwrote any local-only fallback here -- the caller would navigate to
    // /cart believing the add succeeded, only to see it silently empty with
    // no error shown. Let the failure propagate so the caller can surface it.
    await cartApi.addItem({
      itemId: item.itemId,
      transactionType: 'rent',
      startDate: item.startDate,
      endDate: item.endDate,
      deliveryOption: item.deliveryOption,
    })
    // Refresh from server to get the canonical cart state
    await get().syncCart()
  },

  removeItem: async (id) => {
    try {
      await cartApi.removeItem(id)
      set({ items: get().items.filter(i => i.id !== id) })
    } catch {
      // Fallback: remove from local state only
      set({ items: get().items.filter(i => i.id !== id) })
    }
  },

  clearCart: async () => {
    try {
      await cartApi.clear()
    } catch {
      // ignore
    }
    set({ items: [] })
  },

  getTotal: () => {
    return get().items.reduce((sum, item) => sum + (Number.isFinite(item.totalPrice) ? item.totalPrice : 0), 0)
  },

  getItemCount: () => get().items.length
}))